import type {
  JurisdictionalUsersModel,
  OrganizationsModel,
  UsersModel,
} from '@/features/tenant-manager/models';
import { ITEM_SPLITTER, findUpperRecursive, stringToBoolean } from '@/utils';
import type { z } from 'zod';
import {
  type FundAdditionalAttributesModel,
  InspectionEvaluationKsFormSchema,
  KsFormSchema,
  PartialProjectFormSchema,
  type ProjectFormSchema,
  ProjectModel,
} from '../../projects/_models/project';

type FormValues = z.infer<typeof ProjectFormSchema>;

export const getInitialAdditionalJurisdictionalUsersData = (
  organizationIds: string[][],
  additionalJurisdictionalUsers: z.infer<
    typeof FundAdditionalAttributesModel.shape.additionalJurisdictionalUsers
  >,
): Array<{
  ministryId: string;
  organizationIds: string[];
  peopleInChargeIds: string[];
  businessManagerIds: string[];
}> => {
  return (
    additionalJurisdictionalUsers?.map((additionalJurisdictionalUser) => ({
      ministryId: additionalJurisdictionalUser.ministryId,
      organizationIds:
        organizationIds.find(
          (ids) => ids.at(-1) === additionalJurisdictionalUser.organizationId,
        ) ?? [],
      peopleInChargeIds: additionalJurisdictionalUser.peopleInChargeIds,
      businessManagerIds: additionalJurisdictionalUser.businessManagerIds,
    })) ?? []
  );
};

const _getAdditionalJurisdictionalUsers = (
  jurisdictionalUsers: z.infer<typeof JurisdictionalUsersModel>,
  organizations: z.infer<typeof OrganizationsModel>,
  additionalJurisdictionalUsers: z.infer<
    typeof FundAdditionalAttributesModel.shape.additionalJurisdictionalUsers
  >,
) => {
  if (!additionalJurisdictionalUsers) {
    return [];
  }

  return additionalJurisdictionalUsers.reduce<
    Array<{
      ministryId: string;
      organizationName: string;
      businessManagerNames: string[];
      peopleInChargeNames: string[];
    }>
  >((acc, cur) => {
    const filteredPeopleInCharges = jurisdictionalUsers.peopleInCharge?.filter(
      (v) => cur.peopleInChargeIds?.includes(v.id),
    );

    const filteredBusinessManagers =
      jurisdictionalUsers.businessManagers?.filter((v) =>
        cur.businessManagerIds.includes(v.id),
      );

    acc.push({
      ministryId: cur.ministryId,
      peopleInChargeNames: filteredPeopleInCharges?.map((v) => v.name) ?? [],
      businessManagerNames: filteredBusinessManagers?.map((v) => v.name) ?? [],
      organizationName: findUpperRecursive(
        cur.organizationId,
        organizations,
        [],
      )
        .map((v) => v.name)
        .reverse()
        .join(ITEM_SPLITTER),
    });
    return acc;
  }, []);
};

const _getAdditionalJurisdictionalUserNames = (
  users: z.infer<typeof UsersModel>,
  organizations: z.infer<typeof OrganizationsModel>,
  additionalJurisdictionalUsers: z.infer<
    typeof FundAdditionalAttributesModel.shape.additionalJurisdictionalUsers
  >,
) => {
  if (!additionalJurisdictionalUsers || users.length === 0) {
    return [];
  }

  return additionalJurisdictionalUsers.reduce<
    Array<{
      ministryId: string;
      organizationName: string;
      businessManagerNames: string[];
      peopleInChargeNames: string[];
    }>
  >((acc, cur) => {
    const filteredPeopleInCharges = users.filter((v) =>
      cur.peopleInChargeIds?.includes(v.id),
    );

    const filteredBusinessManagers = cur.businessManagerIds.flatMap((d) =>
      users.filter((v) => d === v.id),
    );

    acc.push({
      ministryId: cur.ministryId,
      peopleInChargeNames: filteredPeopleInCharges.map((v) => v.name),
      businessManagerNames: filteredBusinessManagers.map((v) => v.name),
      organizationName: findUpperRecursive(
        cur.organizationId,
        organizations,
        [],
      )
        .map((v) => v.name)
        .reverse()
        .join(ITEM_SPLITTER),
    });
    return acc;
  }, []);
};

export const convertFormSchemaToRequestProjectMode = ({
  values,
  type,
}: {
  values: FormValues & { id: string };
  type: 'update' | 'split';
}) => {
  const { data, sheetType } = PartialProjectFormSchema.parse(values);
  if (!data) {
    return null;
  }

  const {
    organizationIds,
    fiscalYear,
    nestedOtherResponsibleOrganizationIds,
    ...rest
  } = data;

  const { id, ...parsedAdditional } = ProjectModel.pick({
    id: true,
    fiscalYear: true,
    startYear: true,
    endYear: true,
  }).parse({
    id: values.id,
    fiscalYear: fiscalYear?.year(),
    startYear: rest.period?.[0]?.year() ?? null,
    endYear: rest.period?.[1]?.year() ?? null,
  });

  const otherResponsibleOrganizationIds = [
    ...new Set(
      nestedOtherResponsibleOrganizationIds?.flatMap((ids) => {
        const lastId = ids.at(-1);
        return lastId ? [lastId] : [];
      }) ?? [],
    ),
  ];

  const relatedOrganizationIds =
    data.nestedRelatedOrganizationIds?.flatMap((relatedOrganizationIds) => {
      const lastId = relatedOrganizationIds.at(-1);
      return lastId ? [lastId] : [];
    }) ?? [];

  const inspectionEvaluation = type === 'update' ? undefined : {};

  if (sheetType === 'RS') {
    return {
      sheetType,
      ...rest,
      ...parsedAdditional,
      ...(organizationIds && {
        organizationId: organizationIds.at(-1),
      }),
      otherResponsibleOrganizationIds,
      relatedOrganizationIds,
      ...(data.policiesForProject && {
        policiesForProject: data.policiesForProject.map((policyForProject) => ({
          ...policyForProject,
          policyId: policyForProject.policyId.at(-1),
        })),
      }),
      ...(data.skipAdministrativeReformTeamEvaluation && {
        majorExpenditureBudgetPurposeId:
          data.majorExpenditureBudgetPurposeIds.at(-1),
      }),
      inspectionEvaluation,
    };
  }

  if (sheetType === 'SS') {
    return {
      sheetType,
      ...rest,
      ...parsedAdditional,
      ...(organizationIds && {
        organizationId: organizationIds.at(-1),
      }),
      otherResponsibleOrganizationIds,
      relatedOrganizationIds,
      ...(data.independentAdministrativeAgencyIds && {
        independentAdministrativeAgencyId:
          data.independentAdministrativeAgencyIds.at(-1),
      }),
      inspectionEvaluation,
    };
  }

  const {
    data: { fundAdditionalAttributes, ...ksRest },
  } = KsFormSchema.parse(values);
  const parsedInspectionEvaluation = InspectionEvaluationKsFormSchema.safeParse(
    data.inspectionEvaluation,
  );

  return {
    sheetType,
    ...ksRest,
    ...parsedAdditional,
    ...(organizationIds && {
      organizationId: organizationIds.at(-1),
    }),
    relatedOrganizationIds,
    otherResponsibleOrganizationIds,
    ...(fundAdditionalAttributes && {
      fundAdditionalAttributes: {
        ...fundAdditionalAttributes,
        additionalJurisdictionalUsers:
          fundAdditionalAttributes.additionalJurisdictionalUsers?.flatMap(
            (value) =>
              value.organizationIds?.length
                ? [
                    {
                      ministryId: value.ministryId,
                      organizationId: value.organizationIds.at(-1),
                      peopleInChargeIds: value.peopleInChargeIds,
                      businessManagerIds: value.businessManagerIds,
                    },
                  ]
                : [],
          ) ?? [],
        scheduledEnd: {
          ...fundAdditionalAttributes.scheduledEnd,
          endDate:
            fundAdditionalAttributes.scheduledEnd?.endDate?.format(
              'YYYY-MM-DD',
            ),
          endDateOfNewApplicationReceiving:
            fundAdditionalAttributes.scheduledEnd?.endDateOfNewApplicationReceiving?.format(
              'YYYY-MM-DD',
            ),
        },
      },
    }),
    inspectionEvaluation:
      type !== 'update'
        ? {}
        : parsedInspectionEvaluation.success
          ? {
              ...parsedInspectionEvaluation.data,
              lowUsedFundInvestigation: {
                ...parsedInspectionEvaluation.data.lowUsedFundInvestigation,
                hasCeasedOperations: stringToBoolean(
                  parsedInspectionEvaluation.data.lowUsedFundInvestigation
                    .hasCeasedOperations,
                ),
                hasNoResultRecent: stringToBoolean(
                  parsedInspectionEvaluation.data.lowUsedFundInvestigation
                    .hasNoResultRecent,
                ),
                hasFundThatLostPurpose: stringToBoolean(
                  parsedInspectionEvaluation.data.lowUsedFundInvestigation
                    .hasFundThatLostPurpose,
                ),
                hasExceedOneSignificantly: stringToBoolean(
                  parsedInspectionEvaluation.data.lowUsedFundInvestigation
                    .hasExceedOneSignificantly,
                ),
                hasDeemedUnlikelyToBeUsed: stringToBoolean(
                  parsedInspectionEvaluation.data.lowUsedFundInvestigation
                    .hasDeemedUnlikelyToBeUsed,
                ),
              },
            }
          : undefined,
  };
};
